import type { trackEcommerce } from '@svs/nova-core/nextjs/analytics';

import type { DataTransferObject } from '../../@types/DataTransferObject.js';
import type { PaidPayableItem } from '../../@types/Service.js';
import { logger } from '../../utils/index.js';
import type { Wager } from '../../wager/Wager.js';
import { type PaymentConfig, PaymentService } from '../PaymentService.js';
import { postMarketplaceWager } from './services/postMarketplaceWager.js';
import { postWager } from './services/postWager.js';
import { type TrackingContext, trackWagers } from './utils/trackWagers.js';

/**
 * Service for handling wagers
 *
 * @class
 * @augments PaymentService
 * @alias svs.components.wager.serviceWager.WagerService
 * @example
 * const { WagerService } = svs.components.wager.serviceWager;
 * const wagerService = new WagerService();
 * wagerService.pay(wager);
 */
export class WagerService extends PaymentService<Wager> {
  private _accountBalance?: number;

  private _totalCost = 0;

  private readonly _trackingContext?: TrackingContext;

  private readonly _trackTransaction?: typeof trackEcommerce;

  constructor(paymentContext?: PaymentConfig, trackingContext?: TrackingContext) {
    super(paymentContext);
    this._trackingContext = trackingContext;
    this._trackTransaction = paymentContext?.trackTransaction;
  }

  async processRequest(wager: Wager): Promise<DataTransferObject> {
    const isMarketplaceWager = wager.getIsMarketplaceWager();

    let postWagerFunc = postWager;
    if (isMarketplaceWager) {
      postWagerFunc = postMarketplaceWager;
    }

    const dto = await postWagerFunc(wager);

    this._accountBalance = dto.balance ?? this._accountBalance;
    this._totalCost += dto.cost;

    return dto;
  }

  onPaymentDone(paidWagersData: readonly PaidPayableItem<Wager>[]): void {
    const lastPaidItem = paidWagersData.at(-1);

    const hasNewBalance = Boolean(lastPaidItem);

    logger.debug('Payment done callback', {
      hasNewBalance,
      balance: lastPaidItem?.dto.balance,
      wagerId: lastPaidItem?.payable.localId,
    });

    // hantera marketplace wager
    // if (hasNewBalance && userSession.hasRole(userSession.roles.PLAYER)) {
    //   accountBalance.set(lastPaidItem.dto.balance);
    // }
  }

  trackPayments(wagersData: readonly PaidPayableItem<Wager>[]): void {
    void trackWagers(wagersData, this._trackingContext, this._trackTransaction);
  }
}
